import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { listCountsApi } from 'api/inventory';
import colors from 'common/constants/colors';
import DateRangePickerField from 'common/forms/DateRangePickerField';

const PAGE_SIZE = 25;

const VarianceChip = ({ value }) => {
  if (value > 0) {
    return <Chip size='small' color='success' label={`+${value}`} />;
  }
  if (value < 0) {
    return <Chip size='small' color='error' label={`−${Math.abs(value)}`} />;
  }
  return <Chip size='small' label='0' />;
};

VarianceChip.propTypes = { value: PropTypes.number };
VarianceChip.defaultProps = { value: 0 };

const STATUS_CHIP_PROPS = {
  COMPLETED: { color: 'success', label: 'Completed' },
  CANCELLED: { color: 'default', label: 'Cancelled' },
  IN_PROGRESS: {
    sx: { bgcolor: colors.warningAmber, color: 'text.primary' },
    label: 'In Progress',
  },
};

const humanizeStatus = (status) =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const StatusChip = ({ status }) => {
  const { color, sx, label } = STATUS_CHIP_PROPS[status] ?? {
    color: 'default',
    label: status ? humanizeStatus(status) : '—',
  };
  return <Chip size='small' color={color} sx={sx} label={label} />;
};

StatusChip.propTypes = { status: PropTypes.string };
StatusChip.defaultProps = { status: null };

const CountHistoryModal = ({ open, onClose, itemData }) => {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [dateRange, setDateRange] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({ fromDate: '', toDate: '' });

  const inventoryItemId = itemData?.inventoryItemId;

  const fetchCounts = useCallback(async () => {
    if (!inventoryItemId) return;
    setLoading(true);
    try {
      const dateFilter = {};
      if (appliedFilter.fromDate) {
        dateFilter.fromDate = new Date(`${appliedFilter.fromDate}T00:00:00`).toISOString();
      }
      if (appliedFilter.toDate) {
        dateFilter.toDate = new Date(`${appliedFilter.toDate}T23:59:59.999`).toISOString();
      }
      const result = await listCountsApi({
        filter: { inventoryItemId, ...dateFilter },
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setCounts(result?.counts ?? []);
      setTotalCount(result?.totalCount ?? 0);
    } finally {
      setLoading(false);
    }
  }, [inventoryItemId, page, appliedFilter]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setDateRange([]);
      setAppliedFilter({ fromDate: '', toDate: '' });
    }
  }, [open]);

  useEffect(() => {
    if (open) fetchCounts();
  }, [open, fetchCounts]);

  const handleApply = () => {
    setPage(0);
    setAppliedFilter({ fromDate: dateRange[0] ?? '', toDate: dateRange[1] ?? '' });
  };

  const handleClear = () => {
    setDateRange([]);
    setPage(0);
    setAppliedFilter({ fromDate: '', toDate: '' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ bgcolor: '#0d1f35', color: '#fff', pr: 6 }}>
        <Typography
          variant='caption'
          component='div'
          sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', letterSpacing: '0.08em' }}
        >
          COUNT HISTORY
        </Typography>
        <Typography variant='h6' component='div' fontWeight={700} sx={{ color: '#fff' }}>
          {itemData?.drugName || '—'}
          {itemData?.strength ? ` ${itemData.strength}` : ''}
        </Typography>
        <Typography variant='body2' component='div' sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {itemData?.ndc}
          {itemData?.manufacturer ? ` · ${itemData.manufacturer}` : ''} · {itemData?.facilityName}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          aria-label='close'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 240 }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}
            >
              DATE RANGE
            </Typography>
            <DateRangePickerField
              label=''
              initialValue={dateRange}
              onDateChange={setDateRange}
              maxDate={new Date()}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2.5 }}>
            <Button
              variant='outlined'
              size='small'
              onClick={handleClear}
              sx={{ minWidth: 100, height: 35 }}
            >
              Reset
            </Button>
            <Button
              variant='contained'
              size='small'
              onClick={handleApply}
              sx={{ minWidth: 100, height: 35 }}
            >
              Apply
            </Button>
          </Box>
        </Box>

        <Box sx={{ minHeight: 360 }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 360,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align='right'>System Qty</TableCell>
                      <TableCell align='right'>Counted Qty</TableCell>
                      <TableCell align='center'>Variance</TableCell>
                      <TableCell>Counted By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {counts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
                            No count history found for this item.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      counts.map((row) => (
                        <TableRow key={row.inventoryCountId}>
                          <TableCell>
                            {row.createdTimestamp
                              ? format(new Date(row.createdTimestamp), 'MM/dd/yyyy HH:mm')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {row.status !== 'IN_PROGRESS' && row.modifiedTimestamp
                              ? format(new Date(row.modifiedTimestamp), 'MM/dd/yyyy HH:mm')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell align='right'>{row.systemQty}</TableCell>
                          <TableCell align='right'>{row.countedQty}</TableCell>
                          <TableCell align='center'>
                            <VarianceChip value={row.varianceQty} />
                          </TableCell>
                          <TableCell>{row.countedByName || row.countedBy || '—'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CountHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  itemData: PropTypes.shape({
    drugName: PropTypes.string,
    strength: PropTypes.string,
    ndc: PropTypes.string,
    manufacturer: PropTypes.string,
    facilityName: PropTypes.string,
    inventoryItemId: PropTypes.string,
    fulfillingPharmacyId: PropTypes.string,
  }),
};
CountHistoryModal.defaultProps = {
  itemData: null,
};

export default CountHistoryModal;
